import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, Lock, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const passwordRef = useRef();

  // Jump to password field once 10 digits are entered
  useEffect(() => {
    if (mobile.length === 10) {
      passwordRef.current?.focus();
    }
  }, [mobile]);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/login', { mobile, password });

      // Check if role selection is required
      if (response.data.require_selection) {
        navigate('/login/select-role', { state: { mobile, password, options: response.data.options } });
        return;
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');

      // Route based on role
      if (response.data.user.role === 'super_admin') {
        navigate('/superadmin');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-slide-in">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="https://cfms.lk/img/itsignature_logo_white.png" 
              alt="IT Signature Logo" 
              className="h-20 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            IT Signature ERP
          </h1>
          <p className="text-blue-100">Employee Resource Planning System</p>
        </div>

        <Card className="shadow-2xl border-0 animate-slide-in backdrop-blur-lg bg-white/90" data-testid="login-card">
          <CardHeader className="space-y-1 pb-4 pt-6 px-6">
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Welcome Back
            </CardTitle>
            <CardDescription>
              Enter your mobile number and password to login
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Office Mobile Number</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <Phone className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    data-testid="mobile-input"
                    type="tel"
                    placeholder="0771234567"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    className="pl-12 h-14 text-2xl font-semibold tracking-wider border-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    required
                    autoComplete="username"
                    autoFocus
                  />
                  {mobile.length > 0 && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        mobile.length === 10 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {mobile.length}/10
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">Enter your 10-digit Sri Lankan mobile number</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <Input
                    data-testid="password-input"
                    ref={passwordRef}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pl-12 pr-12 h-14 text-lg border-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                data-testid="login-button"
                type="submit"
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={loading || mobile.length !== 10 || !password}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Logging in...
                  </span>
                ) : (
                  <>
                    Login
                    <span className="ml-2">→</span>
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-white/80 mt-6 space-y-1 backdrop-blur-sm bg-white/10 rounded-xl p-4">
          <p className="font-medium text-white">IT Signature (Pvt) Ltd</p>
          <p className="text-blue-100">Support Hotline: 011 4848 988 | 077 3966 920</p>
        </div>
      </div>
    </div>
  );
}
